/**
 * User Service - Firestore user management
 *
 * Manages user approval workflow:
 * - New users are created with status 'pending'
 * - Admin can approve/reject users
 * - Only approved users can access the app
 */

import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';

// User status types
export type UserStatus = 'pending' | 'approved' | 'rejected';

// User document interface
export interface UserDocument {
  uid: string;
  email: string;
  firstName: string;
  lastName: string;
  localAssociation?: string;
  status: UserStatus;
  credits: number;  // Number of analysis credits (1 credit = 1 analysis)
  createdAt: Timestamp;
  updatedAt: Timestamp;
  approvedAt?: Timestamp;
  approvedBy?: string;
  rejectedAt?: Timestamp;
  rejectedBy?: string;
}

// Collection name
const USERS_COLLECTION = 'users';

/**
 * Create a new user document after registration
 */
export async function createUserDocument(
  uid: string,
  email: string,
  firstName: string,
  lastName: string,
  localAssociation?: string
): Promise<void> {
  const userRef = doc(db, USERS_COLLECTION, uid);

  const userData: Record<string, any> = {
    uid,
    email,
    firstName,
    lastName,
    status: 'pending',
    credits: 0,  // New users start with 0 credits - must purchase
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  // Only add localAssociation if provided
  if (localAssociation && localAssociation.trim()) {
    userData.localAssociation = localAssociation.trim();
  }

  await setDoc(userRef, userData);
}

/**
 * Get user document by UID
 * Automatically adds credits field if missing (for existing users before credit system)
 */
export async function getUserDocument(uid: string): Promise<UserDocument | null> {
  const userRef = doc(db, USERS_COLLECTION, uid);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    const data = userSnap.data() as UserDocument;

    // Auto-initialize credits for existing users who don't have the field
    if (data.credits === undefined || data.credits === null) {
      // Set default credits for existing users (0 credits - must purchase)
      try {
        await updateDoc(userRef, { credits: 0, updatedAt: serverTimestamp() });
        data.credits = 0;
        console.log(`[userService] Auto-initialized credits for user ${uid}`);
      } catch (err) {
        console.warn(`[userService] Could not auto-initialize credits for user ${uid}:`, err);
        data.credits = 0;  // Fallback - user sees 0 credits
      }
    }

    return data;
  }

  return null;
}

/**
 * Check if user is approved
 */
export async function isUserApproved(uid: string): Promise<boolean> {
  const user = await getUserDocument(uid);
  return user?.status === 'approved';
}

/**
 * Get user status
 */
export async function getUserStatus(uid: string): Promise<UserStatus | null> {
  const user = await getUserDocument(uid);
  return user?.status || null;
}

/**
 * Get all pending users (for admin)
 * Note: Sorting done in-memory to avoid requiring composite index
 */
export async function getPendingUsers(): Promise<UserDocument[]> {
  const usersRef = collection(db, USERS_COLLECTION);
  const q = query(
    usersRef,
    where('status', '==', 'pending')
  );

  const snapshot = await getDocs(q);
  const users = snapshot.docs.map(doc => doc.data() as UserDocument);

  // Sort by createdAt descending in memory
  return users.sort((a, b) => {
    const aTime = a.createdAt?.toMillis() || 0;
    const bTime = b.createdAt?.toMillis() || 0;
    return bTime - aTime;
  });
}

/**
 * Get all users (for admin)
 * Note: Sorting done in-memory to avoid requiring composite index
 */
export async function getAllUsers(): Promise<UserDocument[]> {
  const usersRef = collection(db, USERS_COLLECTION);

  const snapshot = await getDocs(usersRef);
  const users = snapshot.docs.map(doc => doc.data() as UserDocument);

  // Sort by createdAt descending in memory
  return users.sort((a, b) => {
    const aTime = a.createdAt?.toMillis() || 0;
    const bTime = b.createdAt?.toMillis() || 0;
    return bTime - aTime;
  });
}

/**
 * Approve a user (admin only)
 */
export async function approveUser(uid: string, adminEmail: string): Promise<void> {
  const userRef = doc(db, USERS_COLLECTION, uid);

  await updateDoc(userRef, {
    status: 'approved',
    approvedAt: serverTimestamp(),
    approvedBy: adminEmail,
    updatedAt: serverTimestamp()
  });
}

/**
 * Reject a user (admin only)
 */
export async function rejectUser(uid: string, adminEmail: string): Promise<void> {
  const userRef = doc(db, USERS_COLLECTION, uid);

  await updateDoc(userRef, {
    status: 'rejected',
    rejectedAt: serverTimestamp(),
    rejectedBy: adminEmail,
    updatedAt: serverTimestamp()
  });
}

/**
 * Delete a user document (admin only)
 */
export async function deleteUserDocument(uid: string): Promise<void> {
  const userRef = doc(db, USERS_COLLECTION, uid);
  await deleteDoc(userRef);
}

/**
 * Update user status
 */
export async function updateUserStatus(uid: string, status: UserStatus): Promise<void> {
  const userRef = doc(db, USERS_COLLECTION, uid);

  await updateDoc(userRef, {
    status,
    updatedAt: serverTimestamp()
  });
}

/**
 * Get user credits
 */
export async function getUserCredits(uid: string): Promise<number> {
  const user = await getUserDocument(uid);
  return user?.credits ?? 0;
}

/**
 * Update user credits (admin only)
 */
export async function updateUserCredits(uid: string, credits: number): Promise<void> {
  const userRef = doc(db, USERS_COLLECTION, uid);

  await updateDoc(userRef, {
    credits,
    updatedAt: serverTimestamp()
  });
}

/**
 * Deduct one credit from user (called before analysis)
 * Returns true if successful, false if insufficient credits
 */
export async function deductCredit(uid: string): Promise<boolean> {
  const user = await getUserDocument(uid);
  if (!user || user.credits <= 0) {
    return false;
  }

  const userRef = doc(db, USERS_COLLECTION, uid);
  await updateDoc(userRef, {
    credits: user.credits - 1,
    updatedAt: serverTimestamp()
  });

  return true;
}
